import { useState, useRef, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import Drawer from '../Common/Drawer';
import { InputFormik, SearchableDropdown, Button, Dropdown, DatePickerInputFormik } from '../Common';
import { ProjectRoleDto, ProjectAssigneesWithUserInfoDto, EmploymentType } from '../../commonTypes/project.types';
import { RoleTypeEnum } from '../../commonTypes/user.types';
import { RateType } from '@cyberlabrs/helpers';
import { validationSchema } from './validationSchema';
import { getCurrencySymbol } from '../../util/general';
import { useTranslation } from '../../hooks/useTranslation';
import { effectiveFromDateToApiString, getUtcTodayYmd } from '../../util/projectAssigneeRate/effectiveFrom';
import { mappedCurrency } from '../ClientForms/utils/general';

const BILLABLE_OPTIONS = [
    { label: 'Billable', value: 'billable' },
    { label: 'Non-billable', value: 'non-billable' },
];

const RATE_TYPE_OPTIONS = [
    { label: 'Hourly', value: RateType.HOURLY },
    { label: 'Fixed', value: RateType.FIXED },
];

const DEFAULT_RATE = '0';
const DEFAULT_EMPLOYMENT_TYPE = EmploymentType.FULL_TIME;
const DEFAULT_RATE_TYPE = RateType.HOURLY;
const DEFAULT_BILLABLE_TYPE = 'billable';
const ALLOWED_MANAGER_ROLE_NAMES = new Set<RoleTypeEnum>([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN, RoleTypeEnum.PM]);
const RATE_INPUT_REGEX = /^\d*(\.\d{0,4})?$/;

const normalizeDateOnly = (value?: string, fallback?: string) => {
    if (!value) return fallback || '';
    const trimmed = value.trim();
    const ymdMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymdMatch) {
        return ymdMatch[1];
    }

    const parsed = DateTime.fromISO(trimmed);
    if (!parsed.isValid) return fallback || value;
    return parsed.toFormat('yyyy-MM-dd');
};

type AvailableMember = {
    label: string;
    value: string;
    imageUrl?: string;
    jobTitle?: string;
    roleName?: RoleTypeEnum;
    wasPreviouslyAssigned?: boolean;
    suggestedStartDate?: string;
    minimumStartDate?: string;
};

type EffectiveFromInitialState = {
    rateEffectiveFrom: Date | null;
    currencyEffectiveFrom: Date | null;
};

interface IMemberAssignment {
    memberId: string;
    role: string | number;
    billableType: string;
    rateType: string;
    employmentType: string;
    currencyCode: string;
    rate: string;
    startDate: string;
}

export type AssignMembersSubmitOptions = {
    rateEffectiveFrom?: string;
    currencyEffectiveFrom?: string;
};

interface IAssignMembersDrawerProps {
    open: boolean;
    onClose: () => void;
    onAssign: (members: IMemberAssignment[], options?: AssignMembersSubmitOptions) => void;
    onUnassign?: () => void;
    availableMembers: AvailableMember[];
    employmentTypes: { label: string; value: string }[];
    projectRoles: ProjectRoleDto[];
    editMode?: boolean;
    editMember?: ProjectAssigneesWithUserInfoDto;
    currency: string;
    projectStartDate?: string;
}

const AssignMembersDrawer = ({
    open,
    onClose,
    onAssign,
    onUnassign,
    availableMembers,
    employmentTypes,
    projectRoles,
    editMode = false,
    editMember,
    currency,
    projectStartDate,
}: IAssignMembersDrawerProps) => {
    const { t } = useTranslation();
    const effectiveFromPlaceholderDate = useMemo(() => DateTime.utc().toFormat('MMMM d, yyyy'), []);
    const defaultEffectiveFromDate = useMemo(() => DateTime.utc().startOf('day').toJSDate(), []);
    const defaultAssigneeStartDate = useMemo(() => {
        const normalizedProjectStart = normalizeDateOnly(projectStartDate);
        if (normalizedProjectStart) {
            return normalizedProjectStart;
        }

        return DateTime.local().toFormat('yyyy-MM-dd');
    }, [projectStartDate]);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const formatRoleName = (name: string) => {
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    const roleOptions = projectRoles.map(role => ({ label: formatRoleName(role.name), value: role.id }));
    const managerRole = projectRoles.find(role => role.name === 'MANAGER');
    const assigneeRole = projectRoles.find(role => role.name === 'ASSIGNEE');

    const getInitialMemberAssignments = (): IMemberAssignment[] => {
        if (editMode && editMember) {
            const isBillable = editMember.billable;
            return [
                {
                    memberId: editMember.userSub,
                    role: editMember.assignedRoles?.[0]?.projectRoleDto?.id?.toString() || '',
                    billableType: isBillable ? DEFAULT_BILLABLE_TYPE : 'non-billable',
                    rateType: (editMember as any).rateType || DEFAULT_RATE_TYPE,
                    employmentType: (editMember as any).employmentType || DEFAULT_EMPLOYMENT_TYPE,
                    currencyCode: editMember.currencyCode || currency,
                    rate: editMember.rate?.toString() || '',
                    startDate: normalizeDateOnly(editMember.startDate, defaultAssigneeStartDate),
                },
            ];
        }
        return [
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                currencyCode: currency,
                rate: DEFAULT_RATE,
                startDate: defaultAssigneeStartDate,
            },
        ];
    };

    const [memberAssignments, setMemberAssignments] = useState<IMemberAssignment[]>(getInitialMemberAssignments());
    const lastFormRef = useRef<HTMLDivElement>(null);
    const initialMemberAssignmentsRef = useRef<IMemberAssignment[] | null>(null);
    const initialEffectiveFromRef = useRef<EffectiveFromInitialState | null>(null);

    const parseEffectiveFromDate = (value?: string): Date | null => {
        const normalized = normalizeDateOnly(value);
        if (!normalized) return null;
        const parsed = DateTime.fromISO(normalized, { zone: 'local' });
        return parsed.isValid ? parsed.startOf('day').toJSDate() : null;
    };

    const getInitialEffectiveFromValues = (): EffectiveFromInitialState => {
        if (!editMode || !editMember) {
            return {
                rateEffectiveFrom: defaultEffectiveFromDate,
                currencyEffectiveFrom: defaultEffectiveFromDate,
            };
        }

        const editRateEffectiveFrom = parseEffectiveFromDate(editMember.rateEffectiveFrom);
        const editCurrencyEffectiveFrom = parseEffectiveFromDate(editMember.currencyEffectiveFrom);

        return {
            rateEffectiveFrom: editRateEffectiveFrom ?? defaultEffectiveFromDate,
            currencyEffectiveFrom: editCurrencyEffectiveFrom ?? defaultEffectiveFromDate,
        };
    };

    const findRole = (roleId: string | number) => {
        return roleOptions.find(role => role.value === roleId) || { label: '', value: '' };
    };

    const findMember = (memberId: string) => {
        return availableMembers.find(member => member.value === memberId) || { label: '', value: '' };
    };

    const isManagerEligibleMember = (memberId: string) => {
        const member = availableMembers.find(item => item.value === memberId);
        return member?.roleName ? ALLOWED_MANAGER_ROLE_NAMES.has(member.roleName) : true;
    };

    const getRoleOptionsForMember = (memberId: string) => {
        if (!managerRole || isManagerEligibleMember(memberId)) {
            return roleOptions;
        }

        return roleOptions.filter(role => role.value !== managerRole.id);
    };

    const findEmploymentType = (typeId: string) => {
        return employmentTypes.find(type => type.value === typeId) || { label: '', value: '' };
    };

    const findBillableType = (billableTypeId: string) => {
        return BILLABLE_OPTIONS.find(type => type.value === billableTypeId) || { label: '', value: '' };
    };

    const findRateType = (rateTypeId: string) => {
        return RATE_TYPE_OPTIONS.find(type => type.value === rateTypeId) || { label: '', value: '' };
    };

    const context = useFormik({
        initialValues: {
            members: memberAssignments,
            ...getInitialEffectiveFromValues(),
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: values => {
            if (editMode && (values.rateEffectiveFrom || values.currencyEffectiveFrom)) {
                const maxYmd = getUtcTodayYmd();
                const rateEffective = effectiveFromDateToApiString(values.rateEffectiveFrom, maxYmd);
                const currencyEffective = effectiveFromDateToApiString(values.currencyEffectiveFrom, maxYmd);
                if (rateEffective === 'INVALID_FUTURE') {
                    void context.setFieldTouched('rateEffectiveFrom', true);
                    void context.setFieldError('rateEffectiveFrom', t('projects.rateCorrectionEffectiveFromFuture'));
                    return;
                }
                if (currencyEffective === 'INVALID_FUTURE') {
                    void context.setFieldTouched('currencyEffectiveFrom', true);
                    void context.setFieldError('currencyEffectiveFrom', t('projects.rateCorrectionEffectiveFromFuture'));
                    return;
                }
                onAssign(
                    values.members,
                    rateEffective || currencyEffective
                        ? {
                              ...(rateEffective ? { rateEffectiveFrom: rateEffective } : {}),
                              ...(currencyEffective ? { currencyEffectiveFrom: currencyEffective } : {}),
                          }
                        : undefined
                );
            } else {
                onAssign(values.members);
            }
            handleClose();
        },
    });

    useEffect(() => {
        if (lastFormRef.current && memberAssignments.length > 1) {
            lastFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [memberAssignments.length]);

    useEffect(() => {
        if (open) {
            const initialAssignments = getInitialMemberAssignments();
            setMemberAssignments(initialAssignments);
            initialMemberAssignmentsRef.current = JSON.parse(JSON.stringify(initialAssignments));
            const initialEffectiveFrom = getInitialEffectiveFromValues();
            initialEffectiveFromRef.current = {
                rateEffectiveFrom: initialEffectiveFrom.rateEffectiveFrom ? new Date(initialEffectiveFrom.rateEffectiveFrom) : null,
                currencyEffectiveFrom: initialEffectiveFrom.currencyEffectiveFrom ? new Date(initialEffectiveFrom.currencyEffectiveFrom) : null,
            };
            setIsAdvancedOpen(false);
            void context.setValues({
                members: initialAssignments,
                ...initialEffectiveFrom,
            });
        }
    }, [open, editMode, editMember, defaultEffectiveFromDate]);

    const handleClose = () => {
        setMemberAssignments([
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                currencyCode: currency,
                rate: DEFAULT_RATE,
                startDate: defaultAssigneeStartDate,
            },
        ]);
        context.resetForm();
        initialMemberAssignmentsRef.current = null;
        initialEffectiveFromRef.current = null;
        setIsAdvancedOpen(false);
        onClose();
    };

    const handleAddMoreMembers = () => {
        setMemberAssignments([
            ...memberAssignments,
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                currencyCode: currency,
                rate: DEFAULT_RATE,
                startDate: defaultAssigneeStartDate,
            },
        ]);
    };

    const handleRemoveMember = (index: number) => {
        const updatedMembers = memberAssignments.filter((_, i) => i !== index);
        setMemberAssignments(updatedMembers);
    };

    const updateMemberField = (index: number, field: keyof IMemberAssignment, value: string) => {
        const updatedMembers = [...memberAssignments];
        if (field === 'rate') {
            const normalizedValue = value.replace(',', '.');
            if (normalizedValue !== '' && !RATE_INPUT_REGEX.test(normalizedValue)) {
                return;
            }
            value = normalizedValue;
        }
        updatedMembers[index][field] = value;

        if (field === 'memberId' && managerRole && updatedMembers[index].role === managerRole.id.toString()) {
            if (!isManagerEligibleMember(value)) {
                updatedMembers[index].role = assigneeRole?.id?.toString() || '';
            }
        }

        if (field === 'billableType' && value === 'non-billable') {
            updatedMembers[index].rateType = DEFAULT_RATE_TYPE;
            updatedMembers[index].employmentType = DEFAULT_EMPLOYMENT_TYPE;
            updatedMembers[index].rate = '';
        } else if (field === 'billableType' && value === 'billable') {
            if (!updatedMembers[index].rate || updatedMembers[index].rate.trim() === '') {
                updatedMembers[index].rate = DEFAULT_RATE;
            }
            if (!updatedMembers[index].employmentType || updatedMembers[index].employmentType.trim() === '') {
                updatedMembers[index].employmentType = DEFAULT_EMPLOYMENT_TYPE;
            }
            if (!updatedMembers[index].rateType) {
                updatedMembers[index].rateType = DEFAULT_RATE_TYPE;
            }
        }

        setMemberAssignments(updatedMembers);
        void context.setFieldValue('members', updatedMembers);
    };

    const handleMemberSelection = (index: number, memberItem: AvailableMember) => {
        const updatedMembers = [...memberAssignments];
        updatedMembers[index].memberId = memberItem.value;
        updatedMembers[index].startDate = memberItem.suggestedStartDate || memberItem.minimumStartDate || defaultAssigneeStartDate;

        if (managerRole && updatedMembers[index].role === managerRole.id.toString()) {
            if (!isManagerEligibleMember(memberItem.value)) {
                updatedMembers[index].role = assigneeRole?.id?.toString() || '';
            }
        }

        setMemberAssignments(updatedMembers);
        void context.setFieldValue('members', updatedMembers);
    };

    const getMinimumStartDateForMember = (memberId: string) => {
        const selectedMember = availableMembers.find(member => member.value === memberId);
        return selectedMember?.minimumStartDate || defaultAssigneeStartDate;
    };

    const getAvailableMembersForIndex = (currentIndex: number) => {
        const selectedMemberIds = memberAssignments
            .map((assignment, index) => (index !== currentIndex ? assignment.memberId : null))
            .filter(id => id !== null && id !== '');

        return availableMembers.filter(member => !selectedMemberIds.includes(member.value));
    };

    const isFormValid = () => {
        return memberAssignments.every(member => {
            const baseValid = member.memberId && member.role && member.billableType && member.currencyCode;
            const hasStartDate = Boolean(member.startDate);
            if (member.billableType === 'non-billable') {
                return baseValid && hasStartDate;
            }
            const normalizedRate = member.rate.trim();
            return (
                baseValid &&
                hasStartDate &&
                member.rateType &&
                member.employmentType &&
                normalizedRate &&
                RATE_INPUT_REGEX.test(normalizedRate) &&
                Number(normalizedRate) > 0
            );
        });
    };

    const hasFormChanges = () => {
        if (!editMode) {
            return true;
        }
        if (!initialMemberAssignmentsRef.current) {
            return true;
        }
        const membersChanged = JSON.stringify(memberAssignments) !== JSON.stringify(initialMemberAssignmentsRef.current);
        const initialEffectiveFrom = initialEffectiveFromRef.current || {
            rateEffectiveFrom: defaultEffectiveFromDate,
            currencyEffectiveFrom: defaultEffectiveFromDate,
        };
        const correctionDateSet =
            DateTime.fromJSDate(context.values.rateEffectiveFrom || defaultEffectiveFromDate)
                .toUTC()
                .toISODate() !==
                DateTime.fromJSDate(initialEffectiveFrom.rateEffectiveFrom || defaultEffectiveFromDate)
                    .toUTC()
                    .toISODate() ||
            DateTime.fromJSDate(context.values.currencyEffectiveFrom || defaultEffectiveFromDate)
                .toUTC()
                .toISODate() !==
                DateTime.fromJSDate(initialEffectiveFrom.currencyEffectiveFrom || defaultEffectiveFromDate)
                    .toUTC()
                    .toISODate();
        return membersChanged || correctionDateSet;
    };

    return (
        <Drawer
            open={open}
            title={editMode ? 'Edit assignee' : 'Assign Member'}
            onClose={handleClose}
            showFooter={true}
            className="assign-members-drawer"
            leftButtons={
                editMode && onUnassign
                    ? [
                          {
                              text: 'Unassign',
                              variant: 'text',
                              onClick: onUnassign,
                          },
                      ]
                    : undefined
            }
            rightButtons={[
                {
                    text: 'Cancel',
                    variant: 'text',
                    onClick: handleClose,
                },
                {
                    text: editMode ? 'Save' : 'Assign Member',
                    variant: 'contained',
                    onClick: () => context.handleSubmit(),
                    disabled: !isFormValid() || (editMode && !hasFormChanges()),
                },
            ]}
        >
            <div className="assign-members-content">
                {editMode && editMember && (
                    <div className="edit-member__info">
                        <img
                            src={editMember.imageUrl || '/svg/person.svg'}
                            alt={`${editMember.givenName} ${editMember.familyName}`}
                            className="edit-member__avatar"
                            onError={e => ((e.target as HTMLImageElement).src = '/svg/person.svg')}
                        />
                        <div className="edit-member__name">
                            {`${editMember.givenName || ''} ${editMember.familyName || ''}`.trim() || 'Unknown Member'}
                        </div>
                    </div>
                )}
                {!editMode && availableMembers.some(member => member.wasPreviouslyAssigned) && (
                    <p className="paragraph-5 assign-members-drawer__effective-from-hint">{t('projects.previouslyAssignedHint')}</p>
                )}
                {memberAssignments.map((member, index) => (
                    <div key={index} className="member-assignment-form" ref={index === memberAssignments.length - 1 ? lastFormRef : null}>
                        {!editMode && (
                            <SearchableDropdown
                                label="Select Member"
                                menuItems={getAvailableMembersForIndex(index)}
                                selected={findMember(member.memberId)}
                                onChange={item => handleMemberSelection(index, item as AvailableMember)}
                                isRequired
                                showImage
                            />
                        )}
                        {!editMode && (
                            <Dropdown
                                label="Role"
                                menuItems={getRoleOptionsForMember(member.memberId)}
                                selected={findRole(member.role)}
                                onChange={item => updateMemberField(index, 'role', item.value as string)}
                            />
                        )}
                        <Dropdown
                            label="Billable Type"
                            menuItems={BILLABLE_OPTIONS}
                            selected={findBillableType(member.billableType)}
                            onChange={item => updateMemberField(index, 'billableType', item.value as string)}
                        />

                        {member.billableType === 'billable' && (
                            <Dropdown
                                label="Rate Type"
                                menuItems={RATE_TYPE_OPTIONS}
                                selected={findRateType(member.rateType)}
                                onChange={item => updateMemberField(index, 'rateType', item.value as string)}
                            />
                        )}

                        <SearchableDropdown
                            label="Select Employment Type"
                            menuItems={employmentTypes}
                            selected={findEmploymentType(member.employmentType)}
                            onChange={item => updateMemberField(index, 'employmentType', item.value as string)}
                            isRequired
                            disabled={member.billableType === 'non-billable'}
                            className={member.employmentType === DEFAULT_EMPLOYMENT_TYPE ? 'dropdown--placeholder-value' : ''}
                        />

                        <DatePickerInputFormik
                            className="assign-members-drawer__effective-from-date"
                            context={context}
                            id={`members.${index}.startDate`}
                            label="Start date"
                            labelInfoText="This date is used as the assignee billing start date."
                            placeholder={t('projects.rateCorrectionEffectiveFromPlaceholder', {
                                date: effectiveFromPlaceholderDate,
                            })}
                            date={member.startDate ? DateTime.fromISO(member.startDate).toJSDate() : undefined}
                            changeDate={date => updateMemberField(index, 'startDate', date?.toFormat('yyyy-MM-dd') ?? '')}
                            onPeriodChange={() => {}}
                            enablePastDays={true}
                            isRequired
                            minUtcDateString={getMinimumStartDateForMember(member.memberId)}
                        />

                        <InputFormik
                            context={context}
                            id={`members.${index}.rate`}
                            label={`Rate (${getCurrencySymbol(member.currencyCode || currency)})`}
                            placeholder="Enter Hourly Rate"
                            value={member.rate}
                            onChange={e => updateMemberField(index, 'rate', e.target.value)}
                            isRequired
                            isDisabled={member.billableType === 'non-billable'}
                            className={member.rate === DEFAULT_RATE ? 'input-style--placeholder-value' : ''}
                        />

                        {editMode && (
                            <Dropdown
                                label="Currency"
                                menuItems={mappedCurrency}
                                selected={mappedCurrency.find(item => item.value === member.currencyCode) || { label: '', value: '' }}
                                onChange={item => updateMemberField(index, 'currencyCode', item.value as string)}
                            />
                        )}

                        {editMode && index === 0 && (
                            <div className="assign-members-drawer__effective-from-block">
                                <button
                                    type="button"
                                    className="assign-members-drawer__advanced-toggle"
                                    onClick={() => setIsAdvancedOpen(prev => !prev)}
                                    aria-expanded={isAdvancedOpen}
                                >
                                    <span className="paragraph-4">Advanced settings</span>
                                    <img
                                        src="/img/header2/arrow-down.svg"
                                        alt=""
                                        aria-hidden="true"
                                        className={`assign-members-drawer__advanced-icon ${isAdvancedOpen ? 'is-open' : ''}`}
                                    />
                                </button>
                                {isAdvancedOpen && (
                                    <>
                                        <p className="assign-members-drawer__effective-from-hint paragraph-5">{t('projects.effectiveFromHint')}</p>
                                        <DatePickerInputFormik
                                            className="assign-members-drawer__effective-from-date"
                                            context={context}
                                            id="rateEffectiveFrom"
                                            label="Rate effective from"
                                            labelInfoText="When set, the rate update is applied from this date."
                                            placeholder={t('projects.rateCorrectionEffectiveFromPlaceholder', {
                                                date: effectiveFromPlaceholderDate,
                                            })}
                                            date={context.values.rateEffectiveFrom || undefined}
                                            changeDate={date => void context.setFieldValue('rateEffectiveFrom', date?.toJSDate() ?? null)}
                                            onPeriodChange={() => {}}
                                            enablePastDays={true}
                                            isRequired={false}
                                            maxUtcDateString={getUtcTodayYmd()}
                                            clearData={() => void context.setFieldValue('rateEffectiveFrom', null)}
                                        />
                                        <DatePickerInputFormik
                                            className="assign-members-drawer__effective-from-date"
                                            context={context}
                                            id="currencyEffectiveFrom"
                                            label="Currency effective from"
                                            labelInfoText="When set, the currency update is applied from this date."
                                            placeholder={t('projects.rateCorrectionEffectiveFromPlaceholder', {
                                                date: effectiveFromPlaceholderDate,
                                            })}
                                            date={context.values.currencyEffectiveFrom || undefined}
                                            changeDate={date => void context.setFieldValue('currencyEffectiveFrom', date?.toJSDate() ?? null)}
                                            onPeriodChange={() => {}}
                                            enablePastDays={true}
                                            isRequired={false}
                                            maxUtcDateString={getUtcTodayYmd()}
                                            clearData={() => void context.setFieldValue('currencyEffectiveFrom', null)}
                                        />
                                    </>
                                )}
                            </div>
                        )}

                        {memberAssignments.length > 1 && (
                            <button type="button" className="remove-member-btn" onClick={() => handleRemoveMember(index)}>
                                X
                            </button>
                        )}
                    </div>
                ))}

                {!editMode && (
                    <Button text="Add More Members" variant="text" iconSrc="/svg/plus-blue-without-circle.svg" onClick={handleAddMoreMembers} />
                )}
            </div>
        </Drawer>
    );
};

export default AssignMembersDrawer;
